import React, { useState } from 'react';

import {
  ActionIcon,
  Box,
  Button,
  Center,
  Flex,
  Modal,
  ModalProps,
  Paper,
  SimpleGrid,
  Space,
  Table,
  Text,
  Title,
  createStyles
} from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowLeft, IconTrash } from '@tabler/icons-react';
import { GoogleDriveSelector } from 'components/BatchResourceSelector/GoogleDriveSelector/GoogleDriveSelector';
import { RawTextSelector } from 'components/BatchResourceSelector/RawTextSelector';
import { WebsiteSelector } from 'components/BatchResourceSelector/WebsiteSelector/WebsiteSelector';
import { YoutubeVideoSelector } from 'components/BatchResourceSelector/YoutubeVideoSelector/YoutubeVideoSelector';
import { FileDropzone } from 'components/FileDropzone';
import { InfoComponent } from 'components/InfoComponent';
import { ResourceTile } from 'components/ResourceTile';
import { SyncFrequencySelect } from 'components/SyncFrequencySelect';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import { MAX_FILE_COUNT } from 'shared/constants/general-constants';
import { SLIDING_ANIMATION_DURATION as FADE_ANIMATION_DURATION, PRIMARY_SHADE_DARK } from 'shared/constants/theme';
import { GoogleDriveFileInfo } from 'shared/interfaces/GoogleDrive';
import { ResourceType, SyncFrequency, UnUploadedResource } from 'shared/interfaces/Resource';
import { Website } from 'shared/interfaces/Website';
import { YoutubeVideo } from 'shared/interfaces/Youtube';
import { MIME_TYPE_TO_RESSOURCE_TYPE, getResourceIcon } from 'shared/utils/resource';
import { syncFrequencyOptions } from 'shared/utils/sync';

const useStyles = createStyles((theme) => ({
  container: {
    height: '80svh',
    maxHeight: '825px',
    width: '100%',
    display: 'flex',
    flexDirection: 'row'
  },
  card: {
    width: '170px',
    height: '220px',
    border: `1.5px solid ${theme.colors.gray[3]}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    margin: theme.spacing.lg,

    '&:hover': {
      border: `2px solid ${theme.colors.gray[PRIMARY_SHADE_DARK]}`,
      cursor: 'pointer'
    }
  },
  fadeEnter: {
    opacity: 0,
    willChange: 'opacity'
  },
  fadeEnterActive: {
    opacity: 1,
    transition: `opacity ${FADE_ANIMATION_DURATION}ms ease-in-out`,
    willChange: 'opacity'
  },
  fadeExit: {
    opacity: 1,
    willChange: 'opacity'
  },
  fadeExitActive: {
    opacity: 0,
    transition: `opacity ${FADE_ANIMATION_DURATION}ms ease-in-out`,
    willChange: 'opacity'
  }
}));

enum ActiveSelector {
  SELECTION_TYPE,
  FILE_DROPZONE,
  YOUTUBE,
  GOOGLE_DRIVE,
  WEB,
  TEXT
}

const resourceSelectors = [
  {
    type: ActiveSelector.FILE_DROPZONE,
    icon: getResourceIcon(ResourceType.PDF, 24, 24),
    title: 'RESOURCE_TYPES.PDF'
  },
  {
    type: ActiveSelector.YOUTUBE,
    icon: getResourceIcon(ResourceType.YOUTUBE, 24, 24),
    title: 'RESOURCE_TYPES.YOUTUBE'
  },
  {
    type: ActiveSelector.GOOGLE_DRIVE,
    icon: getResourceIcon(ResourceType.GOOGLE_DRIVE, 24, 24),
    title: 'RESOURCE_TYPES.GOOGLE_DRIVE'
  },
  {
    type: ActiveSelector.WEB,
    icon: getResourceIcon(ResourceType.WEB, 24, 24),
    title: 'RESOURCE_TYPES.WEB'
  },
  {
    type: ActiveSelector.TEXT,
    icon: getResourceIcon(ResourceType.TEXT, 26, 26),
    title: 'RESOURCE_TYPES.TEXT'
  },
  {
    type: ActiveSelector.FILE_DROPZONE,
    icon: getResourceIcon(ResourceType.VIDEO, 24, 24),
    title: 'RESOURCE_TYPES.VIDEO'
  }
];

interface BatchResourceSelectorProps extends ModalProps {
  onBatchUpload: (resources: UnUploadedResource[]) => void;
}

export function BatchResourceSelector({ onBatchUpload, ...modalProps }: BatchResourceSelectorProps) {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [subscription] = useSubscriptionContext();
  const [activeSelector, setActiveSelector] = useState(ActiveSelector.SELECTION_TYPE);
  const [selectedResources, setSelectedResources] = useState<Map<string, UnUploadedResource>>(new Map());
  const isSmallHeight = useMediaQuery(`(max-height: 750px)`);

  const onFilesDrop = (files: FileWithPath[]) => {
    const newSelectedResources = new Map(selectedResources);
    files.map((file) => {
      const fileType = MIME_TYPE_TO_RESSOURCE_TYPE.get(file.type);
      if (!newSelectedResources.has(file.name) && fileType !== undefined) {
        const newFile = {
          fileName: file.name,
          type: fileType,
          fileWithPath: file
        };
        newSelectedResources.set(file.name, newFile);
      }
      setSelectedResources(newSelectedResources);
    });
  };

  const onYoutubeVideoSelect = (youtubeVideo: YoutubeVideo) => {
    const newSelectedResources = new Map(selectedResources);
    if (!newSelectedResources.has(youtubeVideo.id)) {
      newSelectedResources.set(youtubeVideo.id, {
        youtubeVideo: youtubeVideo,
        type: ResourceType.YOUTUBE,
        fileName: youtubeVideo.title
      });
    } else {
      newSelectedResources.delete(youtubeVideo.id);
    }

    setSelectedResources(newSelectedResources);
  };

  const onGoogleDriveFileSelect = (file: GoogleDriveFileInfo) => {
    const newSelectedResources = new Map(selectedResources);
    if (!newSelectedResources.has(file.id)) {
      newSelectedResources.set(file.id, {
        googleDriveFile: file,
        type: ResourceType.GOOGLE_DRIVE,
        fileName: file.name
      });
    } else {
      newSelectedResources.delete(file.id);
    }

    setSelectedResources(newSelectedResources);
  };

  const unSelectResource = (resource: UnUploadedResource) => {
    const newSelectedResources = new Map(selectedResources);
    if (resource.youtubeVideo) {
      newSelectedResources.delete(resource.youtubeVideo.id);
    } else if (resource.googleDriveFile) {
      newSelectedResources.delete(resource.googleDriveFile.id);
    } else {
      newSelectedResources.delete(resource.fileName);
    }

    setSelectedResources(newSelectedResources);
  };

  const onWebsiteAdd = (website: Website) => {
    const newSelectedResources = new Map(selectedResources);
    if (!newSelectedResources.has(website.url)) {
      newSelectedResources.set(website.url, {
        website: website,
        type: ResourceType.WEB,
        fileName: website.url
      });
    }
    setSelectedResources(newSelectedResources);
  };

  const onTextResourceAdd = (file: File) => {
    const fileType = MIME_TYPE_TO_RESSOURCE_TYPE.get(file.type);
    const newSelectedResources = new Map(selectedResources);
    if (!newSelectedResources.has(file.name) && fileType !== undefined) {
      const newFile = {
        fileName: file.name,
        type: fileType,
        fileWithPath: file
      };
      newSelectedResources.set(file.name, newFile);
    }
    setSelectedResources(newSelectedResources);
  };

  const isResourceSelected = (id: string) => selectedResources.has(id);

  const BackToSelection = () => {
    return (
      <Button
        ml={10}
        leftIcon={<IconArrowLeft />}
        sx={{ position: 'absolute', top: 15, left: 5 }}
        onClick={() => setActiveSelector(0)}>
        {t('APP.ACTIONS.BACK')}
      </Button>
    );
  };

  const canUpload = () => {
    return selectedResources.size > 0 && selectedResources.size <= MAX_FILE_COUNT;
  };

  const cleanUp = () => {
    selectedResources.clear();
    setActiveSelector(ActiveSelector.SELECTION_TYPE);
  };

  const batchUploadResources = () => {
    if (!canUpload()) return;
    modalProps.onClose();

    // This is workaround to avoid clearing the resource and flikering before the modal closes
    setTimeout(() => {
      onBatchUpload(Array.from(selectedResources.values()));
      cleanUp();
    }, 200);
  };

  return (
    <Modal
      {...modalProps}
      onClose={() => {
        modalProps.onClose();

        // This is workaround to avoid clearing the resource and flikering before the modal closes
        setTimeout(() => {
          cleanUp();
        }, 200);
      }}
      size="100%"
      sx={{ '.mantine-Modal-content': { maxWidth: '1200px' } }}
      padding={0}
      withCloseButton={false}>
      <Box className={classes.container}>
        <Box sx={{ overflow: 'hidden', width: '100%' }}>
          <CSSTransition
            in={activeSelector === ActiveSelector.SELECTION_TYPE}
            timeout={FADE_ANIMATION_DURATION}
            classNames={{
              enter: classes.fadeEnter,
              enterActive: classes.fadeEnterActive,
              exit: classes.fadeExit,
              exitActive: classes.fadeExitActive
            }}
            unmountOnExit>
            <Flex
              h="100%"
              w={selectedResources.size > 0 ? '100%' : '70%'}
              direction="column"
              justify="center"
              ml="auto"
              mr="auto"
              align="center">
              <Title mb={50} order={2} top={70}>
                {t('BATCH_RESOURCE_SELECTOR.TITLE')}
              </Title>
              <SimpleGrid cols={isSmallHeight ? 2 : 1} spacing="lg">
                {resourceSelectors.map((resourceSelector, key: number) => {
                  return (
                    <Paper
                      shadow="md"
                      key={key}
                      withBorder
                      w="100%"
                      miw={300}
                      p="md"
                      pr={25}
                      onClick={() => setActiveSelector(resourceSelector.type)}
                      sx={{
                        cursor: 'pointer',
                        height: 60,
                        transition: 'transform 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.02)'
                        }
                      }}>
                      <Flex direction="row" align="center" justify="flex-start" w="100%" h="100%">
                        <Box>{resourceSelector.icon}</Box>
                        <Title order={5} ta="center" style={{ flexGrow: 1 }}>
                          {t(resourceSelector.title)}
                        </Title>
                      </Flex>
                    </Paper>
                  );
                })}
              </SimpleGrid>
            </Flex>
          </CSSTransition>
          <CSSTransition
            in={activeSelector === ActiveSelector.FILE_DROPZONE}
            timeout={FADE_ANIMATION_DURATION}
            classNames={{
              enter: classes.fadeEnter,
              enterActive: classes.fadeEnterActive,
              exit: classes.fadeExit,
              exitActive: classes.fadeExitActive
            }}
            unmountOnExit>
            <Center h="100%" sx={() => ({ position: 'relative' })}>
              <BackToSelection />
              <FileDropzone onFilesDrop={onFilesDrop} />
            </Center>
          </CSSTransition>
          <CSSTransition
            in={activeSelector === ActiveSelector.YOUTUBE}
            timeout={FADE_ANIMATION_DURATION}
            classNames={{
              enter: classes.fadeEnter,
              enterActive: classes.fadeEnterActive,
              exit: classes.fadeExit,
              exitActive: classes.fadeExitActive
            }}
            unmountOnExit>
            <Box h="100%" sx={() => ({ position: 'relative' })}>
              <BackToSelection />
              <YoutubeVideoSelector isVideoSelected={isResourceSelected} onVideoSelect={onYoutubeVideoSelect} />
            </Box>
          </CSSTransition>
          <CSSTransition
            in={activeSelector === ActiveSelector.GOOGLE_DRIVE}
            timeout={FADE_ANIMATION_DURATION}
            classNames={{
              enter: classes.fadeEnter,
              enterActive: classes.fadeEnterActive,
              exit: classes.fadeExit,
              exitActive: classes.fadeExitActive
            }}
            unmountOnExit>
            <Box h="100%" sx={() => ({ position: 'relative' })}>
              <BackToSelection />
              <GoogleDriveSelector isFileSelected={isResourceSelected} onFileSelect={onGoogleDriveFileSelect} />
            </Box>
          </CSSTransition>
          <CSSTransition
            in={activeSelector === ActiveSelector.WEB}
            timeout={FADE_ANIMATION_DURATION}
            classNames={{
              enter: classes.fadeEnter,
              enterActive: classes.fadeEnterActive,
              exit: classes.fadeExit,
              exitActive: classes.fadeExitActive
            }}
            unmountOnExit>
            <Box h="100%" sx={() => ({ position: 'relative' })}>
              <BackToSelection />
              <WebsiteSelector onWebsiteAdd={onWebsiteAdd} />
            </Box>
          </CSSTransition>
          <CSSTransition
            in={activeSelector === ActiveSelector.TEXT}
            timeout={FADE_ANIMATION_DURATION}
            classNames={{
              enter: classes.fadeEnter,
              enterActive: classes.fadeEnterActive,
              exit: classes.fadeExit,
              exitActive: classes.fadeExitActive
            }}
            unmountOnExit>
            <Box h="100%" sx={() => ({ position: 'relative' })}>
              <BackToSelection />
              <RawTextSelector onTextResourceAdd={onTextResourceAdd} />
            </Box>
          </CSSTransition>
        </Box>
        <Box
          w={selectedResources.size > 0 ? 400 : 0}
          sx={(theme) => ({
            padding: selectedResources.size > 0 ? '10px 15px' : '0px',
            borderLeft: selectedResources.size > 0 ? `1px solid ${theme.colors.gray[2]}` : 'none',
            overflow: 'hidden',
            flexShrink: 0
          })}>
          {selectedResources.size > 0 && (
            <Flex h="100%" w="100%" direction="column" align="center" justify="space-between">
              <Table w="100%">
                <thead>
                  <tr>
                    <th style={{ borderBottom: 'none' }}>
                      <Text>{t('BATCH_RESOURCE_SELECTOR.SELECTED_RESOURCES')}</Text>
                    </th>
                    <th style={{ borderBottom: 'none' }}>
                      <Flex direction="row" align="center">
                        <Text>{t('BATCH_RESOURCE_SELECTOR.SYNC.TITLE')}</Text>
                        <Space w={5} />
                        <InfoComponent
                          width={300}
                          contentTranslationKey="BATCH_RESOURCE_SELECTOR.SYNC.INFO"
                          position="right"
                        />
                      </Flex>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(selectedResources.values()).map((resource, key: number) => {
                    return (
                      <tr key={key}>
                        <td style={{ border: 'none' }}>
                          <ResourceTile resource={resource} h={35} w={200} fileNameMaxWidth={150} disabled />
                        </td>
                        <td style={{ border: 'none' }}>
                          <Flex direction="row" align="center">
                            <SyncFrequencySelect
                              w={110}
                              defaultSyncFrequency={
                                resource.syncFrequency ? resource.syncFrequency : SyncFrequency.NEVER
                              }
                              onChange={(newValue) => {
                                resource.syncFrequency = newValue as SyncFrequency;
                              }}
                              couldSync={subscription.product.enableSync}
                              resourceType={resource.type}
                              data={syncFrequencyOptions}
                            />
                            <Space w={5} />
                            <ActionIcon onClick={() => unSelectResource(resource)} color="red">
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Flex>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <Flex direction="column" align="center" gap="md">
                {selectedResources.size > MAX_FILE_COUNT && (
                  <Text align="center" size="sm" mt="xs" color="red">
                    {t('FILE_DROPZONE.FILE_COUNT_HINT', { maxFileCount: MAX_FILE_COUNT })}
                  </Text>
                )}
                <Button w={200} onClick={() => batchUploadResources()} disabled={!canUpload()}>
                  {t('APP.ACTIONS.UPLOAD')}
                </Button>
              </Flex>
            </Flex>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
